<template>
  <v-form ref="form" :disabled="loader.loading || events.length === 0">
    <v-row>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="model.event_id"
          label="Evento"
          :items="events"
          :item-title="e => !e ? null :eventName(e)"
          item-value="id"
          variant="outlined"
          density="compact"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="model.pos_id"
          label="Ponto de venda"
          :items="pos"
          item-value="id"
          item-title="name"
          variant="outlined"
          density="compact"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="model.items_per_print"
          label="Items por pré impressão"
          :items="[50, 25, 10, 5]"
          variant="outlined"
          density="compact"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="type"
          label="Tipo de ingresso"
          :items="typeNames"
          variant="outlined"
          density="compact"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-table>
          <thead>
            <tr>
              <th class="text-left">
                Setor
              </th>
              <th class="text-left">
                Seção
              </th>
              <th class="text-left">
                Capacidade
              </th>
              <th class="text-left">
                Disponível
              </th>
              <th class="text-left">
                Preço
              </th>
              <th class="text-left" style="width: 200px">
                Quantidade
              </th>
              <th v-if="type === 'Cortesia'" class="text-left">
                Nome Cortesia
              </th>
              <th v-if="type === 'Gratuidade'" class="text-left">
                Nome Gratuidade
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="section in sections.filter(s => s.ticket_types?.some(t => t.name === type && t.latest_batch))" :key="section.id">
              <td class="text-left">
                {{ section.sector?.name }}
              </td>
              <td class="text-left">
                {{ section.name }}
              </td>
              <td class="text-left">
                {{ section.ticket_types?.find(t => t.name === type)?.section_capacity }}
              </td>
              <td class="text-left">
                {{ sectionRemainingTickets(section) }}
              </td>
              <td class="text-left">
                {{ $formatters.currency(Number(0)) }}
              </td>
              <td class="text-left">
                <v-text-field
                  v-maska="{ mask: '############', postProcess: (v: string) => Math.min(sectionRemainingTickets(section), Number(v)) }"
                  :reverse="false"
                  hide-details
                  variant="outlined"
                  density="compact"
                  @maska="(e: CustomEvent<MaskaDetail>) => model.items.find(i => i.section_id === section.id)!.quantity = Number(e.detail.unmasked)"
                />
              </td>
              <td v-if="type === 'Cortesia' || type === 'Gratuidade'" class="text-left">
                <v-text-field
                  v-model="model.items.find(i => i.section_id === section.id)!.tag"
                  :label="'Nome' + (type === 'Gratuidade' ? ' Gratuidade' : ' Cortesia')"
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
    </v-row>
    <v-row justify="end">
      <v-col md="2">
        <v-btn
          color="primary"
          block
          :loading="loader.loading"
          :disabled="loader.loading || events.length === 0"
          @click="submit"
        >
          Gerar
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts" setup>
import { eventName } from '@/plugins/eventName'
import PrePrintService, { StorePayload } from '@/services/PrePrintService'
import ProducerService from '@/services/ProducerService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import Event from '@/types/Event'
import Section from '@/types/Section'
import { DateTime, Interval } from 'luxon'
import type { MaskaDetail } from 'maska'
import { computed, onMounted, Ref, ref, useTemplateRef, watchEffect } from 'vue'
import type { VForm } from 'vuetify/lib/components'

const loader = useLoader()
const snackbar = useSnackBar()

const events: Ref<Event[]> = ref([])
const sections: Ref<Section[]> = ref([])

const typeNames = computed(() => {
  return [
    ...new Set(
      sections.value.flatMap(s => s.ticket_types?.filter(t => t.latest_batch).map(t => t.name) ?? []),
    ),
  ]
})

const event = computed(() => events.value.find(e => e.id === model.value.event_id) ?? null)
const pos = computed(() => event.value?.pos?.filter(p => p.active) ?? [])
const type = computed({
  get() {
    return model.value.type_name
  },
  set(newValue) {
    model.value.type_name = newValue
  },
})

const model = ref<StorePayload>({
  event_id: '',
  pos_id: '',
  items_per_print: 50,
  items: [],
  type_name: 'Inteira',
} as StorePayload)

const $emit = defineEmits<{
  submit: [model: StorePayload]
}>()

const form = useTemplateRef<VForm>('form')

async function submit() {
  if (!form.value) return

  const { valid } = await form.value.validate()

  if (!valid) {
    useSnackBar().show(
      'Verifique se os campos foram preenchidos corretamente',
      'warning',
    )
    return
  }

  $emit('submit', model.value)
}

function sectionRemainingTickets(section: Section) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- preserving existing behavior; deciding a fallback for a missing ticket_types is a product decision, not a lint fix
  const capacity = section.ticket_types?.[0].section_capacity!
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- see above
  const uses = section.ticket_types?.reduce((prev, curr) => prev + curr.passports_count! + (curr.pos_tickets ?? 0), 0)!
  return Math.max(capacity - uses, 0)
}

watchEffect(async () => {
  if (!model.value.event_id || !model.value.type_name) return

  sections.value = (await (new PrePrintService()).sections(`event_id=${model.value.event_id}`))

  model.value.items = sections.value.flatMap((section) => {
    const types = section.ticket_types?.filter(t => t.name === type.value && t.latest_batch) ?? []

    return types.map(type => ({
      type: type.name,
      quantity: 0,
      batch_id: type.latest_batch?.id,
      section_id: section.id,
      tag: null,
    })) as Pick<StorePayload, 'items'>['items']
  })
})

onMounted(async () => {
  const params = new URLSearchParams({
    end_between: Interval.fromDateTimes(DateTime.now(), DateTime.now().plus({ years: 5 })).toISO(),
  })
  const _events = (await (new ProducerService()).events(1, params.toString(), 100)).data.data
  events.value = _events

  if (_events.length > 0) {
    model.value.event_id = _events[0].id
    model.value.pos_id = _events[0].pos?.[0].id ?? ''
  } else {
    snackbar.show('Nenhum evento disponível no momento.', 'info')
  }
})

</script>
